import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Storage as DatabaseIcon,
  Speed as RealTimeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { integrationApi, syncLogApi } from '../../../services/sisApi';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  action?: React.ReactNode;
}> = ({ title, value, subtitle, icon, color = 'primary', action }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" color={`${color}.main`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" variant="body2">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: `${color}.main` }}>
          {icon}
        </Box>
      </Box>
      {action && (
        <Box sx={{ mt: 2 }}>
          {action}
        </Box>
      )}
    </CardContent>
  </Card>
);

const SyncStatusIndicator: React.FC<{ status: string }> = ({ status }) => {
  const getStatusProps = (status: string) => {
    switch (status) {
      case 'completed':
        return { color: 'success' as const, icon: <CheckIcon />, label: 'Completed' };
      case 'failed':
        return { color: 'error' as const, icon: <ErrorIcon />, label: 'Failed' };
      case 'in_progress':
        return { color: 'warning' as const, icon: <SyncIcon />, label: 'In Progress' };
      case 'partial':
        return { color: 'warning' as const, icon: <WarningIcon />, label: 'Partial' };
      default:
        return { color: 'default' as const, icon: <SyncIcon />, label: status };
    }
  };

  const { color, icon, label } = getStatusProps(status);

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      variant="outlined"
      size="small"
    />
  );
};

const SISDashboard: React.FC = () => {
  // Fetch integration status
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['integrationStatus'],
    queryFn: () => integrationApi.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['sisDashboardStats'],
    queryFn: () => integrationApi.getDashboardStats(),
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch recent sync logs
  const { data: recentSyncs } = useQuery({
    queryKey: ['recentSyncLogs'],
    queryFn: () => syncLogApi.getAll({ page_size: 5 }),
    refetchInterval: 15000, // Refresh every 15 seconds
  });

  const integrationStatus = status?.data;
  const dashboardStats = stats?.data;
  const syncLogs = recentSyncs?.data?.results || [];

  const formatLastSync = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">SIS Integration Dashboard</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetchStatus()}
          disabled={statusLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Connection Status Alert */}
      {integrationStatus && (
        <Alert 
          severity={integrationStatus.is_connected ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          action={
            integrationStatus.is_connected ? (
              <Chip 
                icon={<CheckIcon />} 
                label="Connected" 
                color="success" 
                variant="filled" 
                size="small" 
              />
            ) : (
              <Button color="inherit" size="small">
                Reconnect
              </Button>
            )
          }
        >
          <Typography variant="body2">
            {integrationStatus.is_connected 
              ? 'Successfully connected to SIS database. Real-time synchronization is active.'
              : 'Connection to SIS database failed. Please check configuration settings.'
            }
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Schools"
            value={integrationStatus?.total_schools || 0}
            subtitle="Active schools in SIS"
            icon={<DatabaseIcon fontSize="large" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={integrationStatus?.total_students || 0}
            subtitle="Enrolled students"
            icon={<DatabaseIcon fontSize="large" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Staff"
            value={integrationStatus?.total_staff || 0}
            subtitle="Active staff members"
            icon={<DatabaseIcon fontSize="large" />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Custom Indicators"
            value={integrationStatus?.active_indicators || 0}
            subtitle="Active indicators"
            icon={<TimelineIcon fontSize="large" />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Configuration Stats */}
      {dashboardStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="SIS Configurations"
              value={dashboardStats.active_configurations}
              subtitle={`${dashboardStats.total_configurations} total`}
              icon={<RealTimeIcon fontSize="large" />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Indicator Values"
              value={dashboardStats.total_values}
              subtitle="Calculated values"
              icon={<TimelineIcon fontSize="large" />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Real-time Connections"
              value={dashboardStats.real_time_connections}
              subtitle="Active connections"
              icon={<RealTimeIcon fontSize="large" />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Sync Errors Today"
              value={dashboardStats.sync_errors_today}
              subtitle="Errors in last 24h"
              icon={<ErrorIcon fontSize="large" />}
              color={dashboardStats.sync_errors_today > 0 ? 'error' : 'success'}
            />
          </Grid>
        </Grid>
      )}

      {/* Sync Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Last Synchronization
              </Typography>
              {statusLoading ? (
                <LinearProgress />
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {formatLastSync(integrationStatus?.last_sync)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Next sync will occur based on configuration settings
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Webhooks
              </Typography>
              <Typography variant="h4" color="primary.main">
                {integrationStatus?.pending_webhooks || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Waiting to be processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Sync Logs */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Recent Sync Activities</Typography>
            <Button size="small" href="#/sis-integration/sync-logs">
              View All
            </Button>
          </Box>
          
          {syncLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sync activities found
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Chip label={log.sync_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{log.entity_type}</TableCell>
                      <TableCell>
                        <SyncStatusIndicator status={log.status} />
                      </TableCell>
                      <TableCell>
                        {log.records_processed > 0 ? (
                          <Box>
                            <Typography variant="body2">
                              {log.records_processed} processed
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.records_created} created, {log.records_updated} updated
                              {log.records_failed > 0 && `, ${log.records_failed} failed`}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.started_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.execution_time || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <TimelineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SISDashboard;